import { cn } from '@/lib/utils';
import { useAiAssistantStore, useUIStore } from '@/store';
import { useContractStore, type DeployedContract } from '@/store/contractStore';
import type { ethers } from 'ethers';
import {
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Clock,
    Copy,
    ExternalLink,
    Eye,
    FileCode2,
    PenLine,
    Play,
    Sparkles,
    Trash2,
    XCircle,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

/* ================================================================== */
/*  ABI helpers                                                       */
/* ================================================================== */

interface AbiFunction {
  type: 'function' | 'event' | 'constructor' | 'fallback' | 'receive';
  name?: string;
  stateMutability?: 'pure' | 'view' | 'nonpayable' | 'payable';
  inputs?: Array<{ name: string; type: string }>;
  outputs?: Array<{ name: string; type: string }>;
}

function isReadFunction(fn: AbiFunction): boolean {
  return fn.stateMutability === 'pure' || fn.stateMutability === 'view';
}

function parseFunctions(abi: unknown[]): AbiFunction[] {
  return (abi as AbiFunction[]).filter((item) => item.type === 'function' && item.name);
}

/* ================================================================== */
/*  Contract Interaction Panel                                        */
/* ================================================================== */

export default function ContractInteractionPanel() {
  const { contracts, activeContractId, setActiveContract, removeContract, addInteraction } =
    useContractStore();
  const { sendMessage } = useAiAssistantStore();
  const { setActivePanel } = useUIStore();

  const activeContract = contracts.find((c) => c.id === activeContractId) ?? null;

  if (contracts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center text-[#888]">
        <FileCode2 className="w-12 h-12 mb-3 opacity-30" />
        <p className="text-sm font-medium text-[#aaa]">No Deployed Contracts</p>
        <p className="text-xs mt-1.5 leading-relaxed max-w-[200px]">
          Deploy a contract from the Deploy panel to start interacting with it here.
        </p>
        <button
          onClick={() => setActivePanel('deploy')}
          className="mt-4 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 rounded text-white transition-colors"
        >
          Go to Deploy
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full text-[#cccccc]">
      {/* Contract selector */}
      <div className="p-2 border-b border-[#333]">
        <select
          value={activeContractId ?? ''}
          onChange={(e) => setActiveContract(e.target.value || null)}
          className="w-full px-2 py-1.5 bg-[#1e1e1e] border border-[#444] rounded text-xs text-[#cccccc] focus:border-blue-500 focus:outline-none"
        >
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.contractName} — {c.network} ({c.address.slice(0, 8)}...{c.address.slice(-4)})
            </option>
          ))}
        </select>
      </div>

      {activeContract && (
        <>
          {/* Contract header */}
          <ContractHeader
            contract={activeContract}
            onRemove={() => removeContract(activeContract.id)}
          />

          {/* Lifecycle feedback — AI Improve */}
          <div className="px-3 py-1.5 border-b border-[#333]">
            <button
              onClick={() => {
                setActivePanel('ai');
                const context = `Contract: ${activeContract.contractName}\nAddress: ${activeContract.address}\nNetwork: ${activeContract.network}\nChain ID: ${activeContract.chainId}\n\nABI function count: ${parseFunctions(activeContract.abi).length}\nDeploy gas: ${activeContract.gasUsed ?? 'unknown'}\nRecent interactions: ${activeContract.interactions.length}`;
                sendMessage(
                  `Analyze this deployed contract and suggest improvements:\n\n${context}`,
                  'gas_optimize',
                  { deployedContract: activeContract },
                );
              }}
              className="flex items-center gap-1.5 w-full px-2 py-1.5 text-[10px] rounded bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-colors"
            >
              <Sparkles className="w-3 h-3" />
              <span>AI: Suggest Improvements</span>
              <ArrowRight className="w-3 h-3 ml-auto" />
            </button>
          </div>

          {/* Function list */}
          <div className="flex-1 overflow-y-auto">
            <FunctionList
              contract={activeContract}
              onCallResult={(fn, args, result, error) => {
                addInteraction(activeContract.id, {
                  type: isReadFunction(fn as AbiFunction) ? 'read' : 'write',
                  functionName: fn.name ?? 'unknown',
                  args,
                  result,
                  error,
                });
              }}
            />
          </div>

          {/* Interaction history */}
          {activeContract.interactions.length > 0 && (
            <InteractionHistory interactions={activeContract.interactions} />
          )}
        </>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Contract Header                                                   */
/* ================================================================== */

function ContractHeader({
  contract,
  onRemove,
}: {
  contract: DeployedContract;
  onRemove: () => void;
}) {
  const copyAddress = useCallback(() => {
    navigator.clipboard.writeText(contract.address);
  }, [contract.address]);

  return (
    <div className="px-3 py-2 border-b border-[#333] space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-white">{contract.contractName}</span>
        <button
          onClick={onRemove}
          className="text-[#666] hover:text-red-400 transition-colors"
          title="Remove"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-[#888]">
        <span className="font-mono">
          {contract.address.slice(0, 10)}...{contract.address.slice(-6)}
        </span>
        <button
          onClick={copyAddress}
          className="hover:text-white transition-colors"
          title="Copy address"
        >
          <Copy className="w-2.5 h-2.5" />
        </button>
        <a
          href={`https://etherscan.io/address/${contract.address}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-white transition-colors"
          title="View on explorer"
        >
          <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
      <div className="flex items-center gap-2 text-[9px] text-[#666]">
        <span>{contract.network}</span>
        <span>·</span>
        <span>Chain {contract.chainId}</span>
        {contract.gasUsed && (
          <>
            <span>·</span>
            <span>{contract.gasUsed} gas</span>
          </>
        )}
        <span>·</span>
        <span>{new Date(contract.deployedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Function List                                                     */
/* ================================================================== */

function FunctionList({
  contract,
  onCallResult,
}: {
  contract: DeployedContract;
  onCallResult: (fn: AbiFunction, args: string[], result?: string, error?: string) => void;
}) {
  const functions = useMemo(() => parseFunctions(contract.abi), [contract.abi]);
  const readFns = useMemo(() => functions.filter(isReadFunction), [functions]);
  const writeFns = useMemo(() => functions.filter((f) => !isReadFunction(f)), [functions]);

  return (
    <div className="p-2 space-y-2">
      {/* Read functions */}
      {readFns.length > 0 && (
        <div>
          <div className="flex items-center gap-1 px-1 py-1 text-[10px] font-semibold text-blue-400 uppercase tracking-wider">
            <Eye className="w-3 h-3" />
            <span>Read ({readFns.length})</span>
          </div>
          <div className="space-y-1">
            {readFns.map((fn) => (
              <FunctionRow key={fn.name} fn={fn} contract={contract} onCallResult={onCallResult} />
            ))}
          </div>
        </div>
      )}

      {/* Write functions */}
      {writeFns.length > 0 && (
        <div>
          <div className="flex items-center gap-1 px-1 py-1 text-[10px] font-semibold text-orange-400 uppercase tracking-wider">
            <PenLine className="w-3 h-3" />
            <span>Write ({writeFns.length})</span>
          </div>
          <div className="space-y-1">
            {writeFns.map((fn) => (
              <FunctionRow key={fn.name} fn={fn} contract={contract} onCallResult={onCallResult} />
            ))}
          </div>
        </div>
      )}

      {functions.length === 0 && (
        <p className="text-center text-[10px] text-[#666] py-4">
          No callable functions found in ABI
        </p>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Function Row — expandable with input fields                       */
/* ================================================================== */

function FunctionRow({
  fn,
  contract,
  onCallResult,
}: {
  fn: AbiFunction;
  contract: DeployedContract;
  onCallResult: (fn: AbiFunction, args: string[], result?: string, error?: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [args, setArgs] = useState<string[]>(fn.inputs?.map(() => '') ?? []);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isRead = isReadFunction(fn);

  const handleCall = useCallback(async () => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      // In a real implementation, this calls ethers.js with the contract ABI
      // For now, we simulate the call structure
      const { ethers } = await import('ethers');

      // Get provider — use wallet if available, fallback to default
      let provider: ethers.Provider;
      let signer: ethers.Signer | null = null;

      const ethereum = (window as unknown as Record<string, unknown>).ethereum;
      if (ethereum) {
        provider = new ethers.BrowserProvider(ethereum as ethers.Eip1193Provider);
        if (!isRead) {
          signer = await (provider as ethers.BrowserProvider).getSigner();
        }
      } else {
        provider = ethers.getDefaultProvider();
      }

      const contractInstance = new ethers.Contract(
        contract.address,
        contract.abi as ethers.InterfaceAbi,
        signer ?? provider,
      );

      // Parse args to proper types
      const parsedArgs = args.map((arg, i) => {
        const paramType = fn.inputs?.[i]?.type ?? 'string';
        if (paramType.startsWith('uint') || paramType.startsWith('int')) {
          return arg; // ethers handles string→BigInt conversion
        }
        if (paramType === 'bool') return arg === 'true';
        if (paramType === 'address') return arg;
        return arg;
      });

      const callResult = await contractInstance[fn.name!](...parsedArgs);

      const resultStr =
        typeof callResult === 'object'
          ? JSON.stringify(callResult, (_k, v) => (typeof v === 'bigint' ? v.toString() : v), 2)
          : String(callResult);

      setResult(resultStr);
      onCallResult(fn, args, resultStr);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Call failed';
      setError(errMsg);
      onCallResult(fn, args, undefined, errMsg);
    } finally {
      setLoading(false);
    }
  }, [fn, args, contract.address, contract.abi, isRead, onCallResult]);

  return (
    <div className={cn('rounded border', isRead ? 'border-blue-500/20' : 'border-orange-500/20')}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-1.5 w-full px-2 py-1.5 text-left text-[11px] transition-colors',
          isRead ? 'hover:bg-blue-500/5' : 'hover:bg-orange-500/5',
        )}
      >
        {expanded ? (
          <ChevronDown className="w-3 h-3 shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 shrink-0" />
        )}
        <span className={cn('font-mono', isRead ? 'text-blue-400' : 'text-orange-400')}>
          {fn.name}
        </span>
        {fn.inputs && fn.inputs.length > 0 && (
          <span className="text-[9px] text-[#666]">
            ({fn.inputs.map((inp) => inp.type).join(', ')})
          </span>
        )}
        {fn.outputs && fn.outputs.length > 0 && (
          <span className="text-[9px] text-[#555] ml-auto">
            → {fn.outputs.map((o) => o.type).join(', ')}
          </span>
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-2 pb-2 space-y-1.5">
          {/* Input fields */}
          {fn.inputs?.map((input, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <label className="text-[9px] text-[#888] w-20 shrink-0 text-right">
                {input.name || `arg${i}`}
                <span className="text-[#555] ml-0.5">({input.type})</span>
              </label>
              <input
                type="text"
                value={args[i] ?? ''}
                onChange={(e) => {
                  const newArgs = [...args];
                  newArgs[i] = e.target.value;
                  setArgs(newArgs);
                }}
                placeholder={input.type}
                className="flex-1 px-1.5 py-1 bg-[#1e1e1e] border border-[#444] rounded text-[10px] text-white focus:border-blue-500 focus:outline-none font-mono"
              />
            </div>
          ))}

          {/* Call button */}
          <button
            onClick={handleCall}
            disabled={loading}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors',
              isRead
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-orange-600 hover:bg-orange-500 text-white',
              loading && 'opacity-50 cursor-not-allowed',
            )}
          >
            <Play className="w-3 h-3" />
            {loading ? 'Calling...' : isRead ? 'Query' : 'Transact'}
          </button>

          {/* Result */}
          {result !== null && (
            <div className="px-2 py-1.5 bg-green-500/10 border border-green-500/20 rounded text-[10px] text-green-400 font-mono break-all">
              {result}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded text-[10px] text-red-400 break-all">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Interaction History                                               */
/* ================================================================== */

function InteractionHistory({ interactions }: { interactions: DeployedContract['interactions'] }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? interactions : interactions.slice(0, 3);

  return (
    <div className="border-t border-[#333]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full px-3 py-1.5 text-[10px] text-[#888] hover:text-white transition-colors"
      >
        <Clock className="w-3 h-3" />
        <span>History ({interactions.length})</span>
        {expanded ? (
          <ChevronDown className="w-3 h-3 ml-auto" />
        ) : (
          <ChevronRight className="w-3 h-3 ml-auto" />
        )}
      </button>
      <div className="max-h-32 overflow-y-auto">
        {shown.map((ix) => (
          <div
            key={ix.id}
            className="flex items-center gap-1.5 px-3 py-1 text-[9px] border-t border-[#2a2a2a]"
          >
            {ix.error ? (
              <XCircle className="w-2.5 h-2.5 text-red-400 shrink-0" />
            ) : (
              <CheckCircle2 className="w-2.5 h-2.5 text-green-400 shrink-0" />
            )}
            <span
              className={cn('font-mono', ix.type === 'read' ? 'text-blue-400' : 'text-orange-400')}
            >
              {ix.functionName}
            </span>
            {ix.args.length > 0 && (
              <span className="text-[#555] truncate max-w-[80px]">({ix.args.join(', ')})</span>
            )}
            {ix.result && (
              <span className="text-green-400 ml-auto truncate max-w-[80px]">→ {ix.result}</span>
            )}
            {ix.error && (
              <span className="text-red-400 ml-auto truncate max-w-[80px]">
                ✗ {ix.error.slice(0, 30)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
